import { LegalPage, Section, LegalFooter } from "./legal.privacy";

export function meta() {
  return [{ title: "Data Processing Agreement — Orchestrate" }];
}

export default function DataProcessingAgreement() {
  return (
    <LegalPage title="Data Processing Agreement">
      <Section title="1. Definitions">
        <p>In this Data Processing Agreement (&quot;DPA&quot;):</p>
        <ul>
          <li>
            <strong>&quot;Controller&quot;</strong> means the customer organisation that has agreed
            to the Orchestrate Terms &amp; Conditions and determines the purposes and means of
            processing personal data using the Service.
          </li>
          <li>
            <strong>&quot;Processor&quot;</strong> means APTIM-Solutions Ltd, which processes
            personal data on behalf of the Controller in the course of providing the Service.
          </li>
          <li>
            <strong>&quot;Data Protection Laws&quot;</strong> means the UK General Data Protection
            Regulation (UK GDPR), the Data Protection Act 2018, and (where applicable) the EU
            General Data Protection Regulation (EU GDPR).
          </li>
          <li>
            <strong>&quot;Personal Data&quot;</strong>, <strong>&quot;Data Subject&quot;</strong>,{" "}
            <strong>&quot;Processing&quot;</strong>, and{" "}
            <strong>&quot;Sub-processor&quot;</strong> have the meanings given in Data Protection
            Laws.
          </li>
          <li>
            <strong>&quot;Service&quot;</strong> means the Orchestrate platform as described in the
            Terms &amp; Conditions.
          </li>
        </ul>
      </Section>

      <Section title="2. Scope and Purpose">
        <p>
          This DPA applies to the processing of Personal Data by the Processor on behalf of the
          Controller in connection with the provision of the Service. It supplements and forms part
          of the Orchestrate{" "}
          <a href="/terms" className="text-accent-brand hover:underline">
            Terms &amp; Conditions
          </a>{" "}
          and{" "}
          <a href="/privacy" className="text-accent-brand hover:underline">
            Privacy Policy
          </a>
          .
        </p>
        <p>The Processor shall process Personal Data only to:</p>
        <ul>
          <li>Provide, maintain, and improve the Service</li>
          <li>Authenticate users and manage access control</li>
          <li>Maintain audit trails for governance and compliance</li>
          <li>
            Deliver AI-powered features when invoked by users (see Section 7)
          </li>
          <li>Send transactional communications (invitations, notifications)</li>
          <li>Monitor errors and performance to ensure platform reliability</li>
        </ul>
      </Section>

      <Section title="3. Data Processed">
        <p>The categories of Personal Data processed under this DPA include:</p>
        <ul>
          <li>
            <strong>Identity data:</strong> Full name, email address, organisation membership, and
            role assignments.
          </li>
          <li>
            <strong>Authentication data:</strong> Session tokens and authentication state managed by
            Auth0. We do not store passwords.
          </li>
          <li>
            <strong>Project data:</strong> Information entered into projects, programmes, RAID
            items, governance deliverables, budgets, resources, and tasks. This may include names of
            individuals referenced in project records.
          </li>
          <li>
            <strong>Usage and audit data:</strong> Actions performed within the platform, timestamps,
            and user identifiers for governance audit trails.
          </li>
          <li>
            <strong>Technical data:</strong> Browser type, operating system, IP address, and error
            diagnostics collected by Sentry for error monitoring.
          </li>
        </ul>
        <p>
          Data Subjects include the Controller&apos;s employees, contractors, and other individuals
          whose data is entered into the Service by the Controller&apos;s authorised users.
        </p>
      </Section>

      <Section title="4. Processor Obligations">
        <p>The Processor shall:</p>
        <ul>
          <li>
            Process Personal Data only on documented instructions from the Controller, unless
            required to do so by applicable law (in which case the Processor shall inform the
            Controller of that legal requirement before processing, unless prohibited from doing so).
          </li>
          <li>
            Ensure that persons authorised to process Personal Data have committed themselves to
            confidentiality or are under an appropriate statutory obligation of confidentiality.
          </li>
          <li>
            Implement and maintain appropriate technical and organisational measures to ensure a
            level of security appropriate to the risk, as described in Section 6.
          </li>
          <li>
            Assist the Controller, taking into account the nature of processing, in fulfilling its
            obligations to respond to Data Subject requests under Data Protection Laws.
          </li>
          <li>
            Assist the Controller in ensuring compliance with its obligations regarding security of
            processing, notification of personal data breaches, data protection impact assessments,
            and prior consultation with supervisory authorities.
          </li>
          <li>
            At the choice of the Controller, delete or return all Personal Data after the end of the
            provision of the Service, and delete existing copies unless applicable law requires
            retention. The Controller may request a data export within 30 days of termination.
          </li>
          <li>
            Make available to the Controller all information necessary to demonstrate compliance with
            the obligations set out in this DPA and allow for and contribute to audits, including
            inspections, conducted by the Controller or an auditor mandated by the Controller.
          </li>
        </ul>
      </Section>

      <Section title="5. Sub-processors">
        <p>
          The Controller provides general authorisation for the Processor to engage the following
          Sub-processors. The Processor shall inform the Controller of any intended changes to its
          Sub-processors, giving the Controller the opportunity to object.
        </p>
        <table className="w-full text-sm border-collapse mt-4 mb-4">
          <thead>
            <tr className="border-b border-border-light text-left">
              <th className="py-2 pr-4 font-bold text-navy">Sub-processor</th>
              <th className="py-2 pr-4 font-bold text-navy">Purpose</th>
              <th className="py-2 font-bold text-navy">Location</th>
            </tr>
          </thead>
          <tbody className="[&_tr]:border-b [&_tr]:border-border-light">
            <tr>
              <td className="py-2 pr-4">Auth0 (Okta, Inc.)</td>
              <td className="py-2 pr-4">Identity and authentication</td>
              <td className="py-2">US (EU data region available)</td>
            </tr>
            <tr>
              <td className="py-2 pr-4">Anthropic, PBC</td>
              <td className="py-2 pr-4">AI features (Claude API)</td>
              <td className="py-2">US</td>
            </tr>
            <tr>
              <td className="py-2 pr-4">Microsoft Corporation</td>
              <td className="py-2 pr-4">Session analytics (Clarity)</td>
              <td className="py-2">US / EU</td>
            </tr>
            <tr>
              <td className="py-2 pr-4">Sentry (Functional Software, Inc.)</td>
              <td className="py-2 pr-4">Error monitoring and performance</td>
              <td className="py-2">US</td>
            </tr>
            <tr>
              <td className="py-2 pr-4">Resend, Inc.</td>
              <td className="py-2 pr-4">Transactional email delivery</td>
              <td className="py-2">US</td>
            </tr>
            <tr>
              <td className="py-2 pr-4">Fly.io, Inc.</td>
              <td className="py-2 pr-4">Application hosting and database</td>
              <td className="py-2">EU (London region)</td>
            </tr>
          </tbody>
        </table>
        <p>
          The Processor shall impose data protection obligations on each Sub-processor no less
          protective than those set out in this DPA. The Processor remains fully liable to the
          Controller for the performance of each Sub-processor&apos;s obligations.
        </p>
      </Section>

      <Section title="6. Security Measures">
        <p>
          The Processor implements and maintains the following technical and organisational measures:
        </p>
        <ul>
          <li>
            <strong>Encryption:</strong> All data is encrypted in transit (TLS 1.2+) and at rest.
          </li>
          <li>
            <strong>Access control:</strong> Role-based access control at platform, organisation,
            and project levels. Authentication is managed by Auth0 with support for multi-factor
            authentication.
          </li>
          <li>
            <strong>Multi-tenant isolation:</strong> Each organisation&apos;s data is logically
            isolated. Strict query-level scoping ensures users can only access data belonging to
            their own organisation and authorised projects.
          </li>
          <li>
            <strong>Audit logging:</strong> All significant actions within the platform are recorded
            in an immutable audit trail, including the user, action, timestamp, and affected record.
          </li>
          <li>
            <strong>Infrastructure security:</strong> Application is hosted on Fly.io with automated
            deployments, container isolation, and databases in the EU (London) region.
          </li>
          <li>
            <strong>Dependency management:</strong> Regular security reviews and automated dependency
            updates to address known vulnerabilities.
          </li>
          <li>
            <strong>Error monitoring:</strong> Sentry provides real-time error detection, enabling
            rapid response to issues that could affect data integrity or availability.
          </li>
        </ul>
      </Section>

      <Section title="7. AI Processing (Anthropic)">
        <p>
          Where the Controller&apos;s users invoke AI-powered features, the following additional
          terms apply:
        </p>
        <ul>
          <li>
            Project context data (such as descriptions, RAID items, and governance information) is
            sent to Anthropic&apos;s Claude API solely to generate the requested AI response.
          </li>
          <li>
            Anthropic processes API inputs as a Sub-processor and does not use inputs or outputs for
            model training. This is confirmed by{" "}
            <a
              href="https://www.anthropic.com/policies/privacy"
              target="_blank"
              rel="noopener noreferrer"
              className="text-accent-brand hover:underline"
            >
              Anthropic&apos;s Privacy Policy
            </a>{" "}
            and their API Terms of Service.
          </li>
          <li>
            AI features are optional and must be explicitly invoked by the user. No data is sent to
            Anthropic unless a user actively uses an AI feature.
          </li>
          <li>
            AI features may be disabled entirely by the Controller&apos;s organisation administrator
            or by the Processor at the platform level.
          </li>
          <li>
            The Processor logs prompts and token usage for cost management and usage tracking. These
            logs are scoped to the Controller&apos;s organisation and are subject to the same access
            controls and retention policies as other platform data.
          </li>
        </ul>
      </Section>

      <Section title="8. International Transfers">
        <p>
          The Processor&apos;s primary infrastructure is hosted in the EU (London region). Where
          Personal Data is transferred to Sub-processors located outside the UK or EEA (see Section
          5), the Processor ensures appropriate safeguards are in place, including:
        </p>
        <ul>
          <li>
            Standard Contractual Clauses (SCCs) approved by the European Commission and/or the UK
            International Data Transfer Agreement (IDTA) or UK Addendum to the EU SCCs.
          </li>
          <li>
            The Sub-processor&apos;s participation in recognised data transfer frameworks (e.g. the
            EU-US Data Privacy Framework).
          </li>
          <li>
            Supplementary measures where required by applicable guidance from supervisory
            authorities.
          </li>
        </ul>
      </Section>

      <Section title="9. Data Breach Notification">
        <p>
          In the event of a Personal Data breach, the Processor shall notify the Controller without
          undue delay and in any event within 72 hours of becoming aware of the breach. The
          notification shall include:
        </p>
        <ul>
          <li>A description of the nature of the breach, including the categories and approximate number of Data Subjects and records affected</li>
          <li>The name and contact details of the Processor&apos;s point of contact</li>
          <li>A description of the likely consequences of the breach</li>
          <li>A description of the measures taken or proposed to address the breach, including measures to mitigate its effects</li>
        </ul>
        <p>
          The Processor shall cooperate with the Controller and take reasonable steps to assist in
          the investigation, mitigation, and remediation of the breach.
        </p>
      </Section>

      <Section title="10. Data Retention and Deletion">
        <p>
          The Processor retains Personal Data for as long as the Controller&apos;s account is active
          or as needed to provide the Service. Audit trail data is retained for the lifetime of the
          project for governance and compliance purposes.
        </p>
        <p>Upon termination of the Service:</p>
        <ul>
          <li>
            The Controller may request an export of all Customer Data within 30 days of termination.
          </li>
          <li>
            After the 30-day period, the Processor shall delete or anonymise all Personal Data,
            except where retention is required by applicable law.
          </li>
          <li>
            The Processor shall confirm deletion in writing upon the Controller&apos;s request.
          </li>
        </ul>
      </Section>

      <Section title="11. Data Subject Rights">
        <p>
          The Processor shall assist the Controller in responding to requests from Data Subjects
          exercising their rights under Data Protection Laws, including:
        </p>
        <ul>
          <li>Right of access</li>
          <li>Right to rectification</li>
          <li>Right to erasure (right to be forgotten)</li>
          <li>Right to restriction of processing</li>
          <li>Right to data portability</li>
          <li>Right to object</li>
        </ul>
        <p>
          Where a Data Subject contacts the Processor directly, the Processor shall promptly
          redirect the request to the Controller unless the Processor is legally required to respond
          directly.
        </p>
      </Section>

      <Section title="12. Audit Rights">
        <p>
          The Processor shall make available to the Controller, on reasonable request, all
          information necessary to demonstrate compliance with this DPA. The Controller (or its
          appointed auditor) may conduct audits of the Processor&apos;s processing activities,
          subject to:
        </p>
        <ul>
          <li>Reasonable advance written notice (at least 30 days)</li>
          <li>
            Audits being conducted during normal business hours and in a manner that minimises
            disruption to the Processor&apos;s operations
          </li>
          <li>
            The Controller bearing its own costs of the audit, unless the audit reveals material
            non-compliance by the Processor
          </li>
        </ul>
      </Section>

      <Section title="13. Liability">
        <p>
          The liability of each party under this DPA is subject to the limitations and exclusions of
          liability set out in the{" "}
          <a href="/terms" className="text-accent-brand hover:underline">
            Terms &amp; Conditions
          </a>
          . Nothing in this DPA excludes or limits liability for breaches of Data Protection Laws
          to the extent such liability cannot be excluded or limited under applicable law.
        </p>
      </Section>

      <Section title="14. Term and Termination">
        <p>
          This DPA shall remain in effect for the duration of the Processor&apos;s processing of
          Personal Data on behalf of the Controller. It shall automatically terminate when the
          Processor ceases to process Personal Data on behalf of the Controller, subject to the data
          retention and deletion obligations in Section 10.
        </p>
      </Section>

      <Section title="15. Governing Law">
        <p>
          This DPA shall be governed by and construed in accordance with the laws of England and
          Wales. Any disputes arising under or in connection with this DPA shall be subject to the
          exclusive jurisdiction of the courts of England and Wales.
        </p>
      </Section>

      <Section title="16. Contact">
        <p>
          For questions about this DPA or to exercise any rights under it, please contact us:
        </p>
        <ul>
          <li>
            Email:{" "}
            <a
              href="mailto:privacy@aptim-solutions.com"
              className="text-accent-brand hover:underline"
            >
              privacy@aptim-solutions.com
            </a>
          </li>
          <li>APTIM-Solutions Ltd</li>
          <li>Registered in England and Wales</li>
        </ul>
      </Section>
    </LegalPage>
  );
}
